<template>
  <div class="inspector-panel">

    <section class="inspector-instructions">
      <h4 class="section-title">Как использовать<br>ИИ-инспектор: </h4>
      <Tooltip
        size="2"
        iconChar="?"
        circleBg="var(--blue-1)"
        iconColor="#fff"
        textAlign="center"
        tooltipText="<ol class='instruction-list'>
        <li>Выберите элемент на&nbsp;странице, который хотите проанализировать.</li>
        <li>ИИ&nbsp;мгновенно проведет глубокий анализ выбранного элемента.</li>
        <li>Получите точные рекомендации и&nbsp;полезные инсайты для улучшения элемента.</li>
      </ol>"
      />
    </section>

    <div v-if="localLoading || inspectorLoading" class="inspector-loading">
      Анализируем элемент...
    </div>

    <div v-else-if="localActiveElement" class="inspector-content">
      <section class="inspector-section">
        <h4 class="section-title">Описание</h4>
        <p><strong>ID:</strong> {{ localActiveElement.id || '—' }}</p>
        <p><strong>Тип:</strong> {{ analysisSafe.element || '—' }}</p>
        <p><strong>Краткое описание:</strong> {{ analysisSafe.summary || '—' }}</p>
        <p><strong>Текст / Контент:</strong> {{ localActiveElement.data || '-' }}</p>
        <p><strong>Описание разработчика:</strong> {{ localActiveElement.description || '-' }}</p>
      </section>

      <section class="inspector-section">
        <h4 class="section-title">Стили</h4>
        <ul class="styles-list">
          <li v-for="(value, key) in mergedStyleDetails" :key="key">
            <strong>{{ key }}:</strong> {{ value }}
          </li>
        </ul>
      </section>

      <section class="inspector-section">
        <h4 class="section-title">Рекомендации</h4>

        <div v-if="!hasRecommendations">Нет рекомендаций</div>

        <ol v-else class="tips-list">
          <li v-for="(tip, i) in analysisSafe.recommendations" :key="i">
            {{ tip }}
          </li>
        </ol>
      </section>

      <section v-if="parseError" class="inspector-section">
        <h4 class="section-title">Примечание</h4>
        <p>Не удалось распарсить детальный JSON от ИИ. Показываем доступные поля. Ошибка: {{ parseError }}</p>
        <pre style="white-space:pre-wrap;word-break:break-word">{{ rawInspectorTextPreview }}</pre>
      </section>
    </div>

    <div class="inspector-empty inspector-section" v-if="!localLoading && !inspectorLoading">
      Выберите элемент — ИИ сделает анализ
      <button :disabled="isChoosing" @click="startChoosing()">{{'Выбрать'}}</button>
    </div>

  </div>
</template>

<script>
import Tooltip from "@/components/UI/Info/TooltipIcon.vue";

export default {
  name: 'AiInspectorPanel',
  components: {
    Tooltip
  },
  data() {
    return {
      analysis: null,
      localLoading: false,
      parseError: null,
      rawInspectorTextPreview: '',
      isChoosing: false,
      // Теперь работаем только с localActiveElement
      localActiveElement: null
    }
  },
  computed: {
    // По-прежнему доступ к глобальному activeElement в сторе (чтобы слушать изменения)
    activeElement() {
      return this.$store.getters['activeElement/activeElement'];
    },
    inspectorInfo() {
      return this.$store.getters['inspector/inspectorInfo'];
    },
    inspectorLoading() {
      return this.$store.getters['inspector/inspectorLoading'];
    },
    // Безопасный доступ к полям (чтобы шаблон не падал)
    analysisSafe() {
      return this.analysis || {};
    },
    // Строки со стилями: берем приоритетно из анализа (если AI вернул), иначе из localActiveElement.settings
    mergedStyleDetails() {
      const aiStyles = (this.analysis && this.analysis.styles) ? this.analysis.styles : {};
      const s = this.localActiveElement && this.localActiveElement.settings ? this.localActiveElement.settings : {};
      return {
        'Цвет текста': aiStyles.color || s.color || '-',
        'Фон': aiStyles.backgroundColor || s.backgroundColor || '-',
        'Размер шрифта': formatFontSize(aiStyles.fontSize || s.fontSize),
        'Межстрочный интервал': aiStyles.lineHeight || s.lineHeight || '-',
        'Интервал между буквами': aiStyles.letterSpacing || s.letterSpacing || '-'
      };

      function formatFontSize(v) {
        if (v === undefined || v === null || v === '') return '-';
        // Если число и меньше 6 — это, возможно, rem; если >6 — px. (простая эвристика)
        if (typeof v === 'number') {
          return v <= 6 ? `${v}rem` : `${v}px`;
        }
        return String(v);
      }
    },
    hasRecommendations() {
      return Array.isArray(this.analysisSafe.recommendations) && this.analysisSafe.recommendations.length > 0;
    }
  },
  watch: {
    // Следим за глобальным activeElement, но изменяем localActiveElement только если мы в режиме выбора
    activeElement: {
      immediate: false,
      handler(newEl) {
        if (this.isChoosing && newEl) {
          // когда пользователь нажал "Выбрать" и стор обновил activeElement — сохраняем в localActiveElement
          this.localActiveElement = newEl;
          // сбрасываем режим выбора — выбран элемент
          this.isChoosing = false;
        }
      }
    },

    // когда localActiveElement меняется — запускаем анализ (вне зависимости от isChoosing)
    localActiveElement: {
      immediate: false,
      handler(newLocal) {
        if (newLocal) {
          // очищаем предыдущие данные и запускаем анализ
          this.analysis = null;
          this.parseError = null;
          this.rawInspectorTextPreview = '';
          // Запускаем получение анализа
          this.fetchAIAnalysis(newLocal);
        } else {
          // если localActiveElement сброшен — очистим отображение
          this.analysis = null;
          this.parseError = null;
        }
      }
    },

    // если инспектор обновился извне — попытаться распарсить
    inspectorInfo: {
      handler(newInfo) {
        if (newInfo) {
          this.tryParseInspector(newInfo);
        }
      },
      deep: true
    }
  },
  methods: {
    startChoosing () {
      // включаем режим выбора — следующий приходящий activeElement из стора запишется в localActiveElement
      this.isChoosing = true;
      // опционально: очистим прошлый анализ, чтобы пользователь видел загрузку
      this.analysis = null;
      this.parseError = null;
      this.rawInspectorTextPreview = '';
    },
    async fetchAIAnalysis(element) {
      // теперь fetchAIAnalysis всегда выполняет анализ для переданного элемента
      if (!element) return;

      this.localLoading = true;
      this.parseError = null;
      // не затираем localActiveElement — он уже установлен
      this.analysis = null;
      this.rawInspectorTextPreview = '';

      try {
        // dispatch возвращает данные (наш action возвращяет data или null)
        const res = await this.$store.dispatch('inspector/GET_INSPECTOR_INFO', element);
        // если action не возвращает данные, возьмем их из state
        const inspectorData = res || this.inspectorInfo;
        if (inspectorData) {
          await this.tryParseInspector(inspectorData);
        } else {
          this.parseError = 'Нет ответа от сервера';
        }
      } catch (err) {
        console.error('fetchAIAnalysis error', err);
        this.parseError = err && err.message ? err.message : String(err);
      } finally {
        this.localLoading = false;
      }
    },

    // Универсальная функция для парсинга JSON, который AI возвращает в content (html) или text (code fence)
    tryParseInspector(inspectorData) {
      this.parseError = null;
      this.rawInspectorTextPreview = '';

      // пытаемся получить "сырый" JSON текст в нескольких вариантах
      let jsonText = null;

      // 1) Попытка: content (HTML) -> извлечь текст внутри <code> или <pre>
      if (inspectorData.content) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(inspectorData.content, 'text/html');
          const codeEl = doc.querySelector('code') || doc.querySelector('pre');
          if (codeEl && codeEl.textContent && codeEl.textContent.trim()) {
            jsonText = codeEl.textContent.trim();
          } else {
            // если внутри <pre><code> есть HTML-escaped символы (например &quot;), textContent их декодирует автоматически
            const pre = doc.querySelector('pre');
            if (pre && pre.textContent) jsonText = pre.textContent.trim();
          }
        } catch (e) {
          // не критично — попробуем другие подходы
          console.warn('Парсинг content как HTML не удался', e);
        }
      }

      // 2) Если не получилось, пытаемся вытянуть из inspectorData.text (code fence)
      if (!jsonText && inspectorData.text) {
        // ищем блок между ```json ... ``` или ``` ... ```
        const fenceMatch = inspectorData.text.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
        if (fenceMatch && fenceMatch[1]) {
          jsonText = fenceMatch[1].trim();
        } else {
          // если нет fences, попробуем взять весь текст и удалить тройные обратные кавычки вручную
          jsonText = inspectorData.text.replace(/```/g, '').trim();
        }
      }

      // 3) Если jsonText всё ещё пуст, пробуем найти JSON в любом месте строки (ошибка)
      if (!jsonText && inspectorData.content) {
        // вытянем всё текстовое содержимое (как последняя попытка)
        try {
          const tmp = (new DOMParser()).parseFromString(inspectorData.content, 'text/html');
          jsonText = tmp.body ? tmp.body.textContent.trim() : null;
        } catch (e) {
          // игнор
        }
      }

      // Сохраняем небольшой превью для отладки (покажем в UI, если парсинг не удался)
      this.rawInspectorTextPreview = (jsonText || inspectorData.text || inspectorData.content || '').slice(0, 2000);

      if (!jsonText) {
        this.parseError = 'Не найден JSON в ответе от ИИ';
        return;
      }

      // Попытка JSON.parse
      try {
        const parsed = JSON.parse(jsonText);
        this.analysis = parsed;
        this.parseError = null;
      } catch (err) {
        // Если JSON.parse не прошёл, попробуем исправить распространённые проблемы:
        // - кавычки «`» в ключах/строках
        // - trailing commas
        // Попытка "мягкого" исправления:
        try {
          let relaxed = jsonText
            // удалить trailing запятые перед } или ]
            .replace(/,\s*([}\]])/g, '$1')
            // заменить одинарные кавычки на двойные (осторожно)
            .replace(/([\{\[,:\s])'([^']*)'(?=[\s,\}\]])/g, '$1"$2"');

          // ещё одна попытка распарсить
          const parsedRelaxed = JSON.parse(relaxed);
          this.analysis = parsedRelaxed;
          this.parseError = null;
        } catch (err2) {
          console.error('JSON parse failed', err, err2);
          this.parseError = err2.message || err.message || 'Ошибка при парсинге JSON';
          // не затираем analysis — оставим null
          this.analysis = null;
        }
      }
    }
  }
}
</script>

<style scoped>
.inspector-instructions {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	margin-bottom: 0.5rem;
}
.inspector-panel {
  padding-top: 1rem;
  border-top: 0.2rem solid var(--grey-1);
}

.inspector-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-0);
}

.inspector-empty,
.inspector-loading {
  color: var(--color-0);
  font-style: italic;
}

.inspector-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.inspector-section {
	text-align: left;
	display: flex;
  flex-direction: column;
  gap: 0.5rem;
	align-items: flex-start;
	border-top: 0.2rem solid var(--grey-1);
	padding-top: 0.5rem;
}

.inspector-section p {
	text-align: left;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
  margin-bottom: 0.5rem;
  color: var(--color-0);
}

.styles-list,
.tips-list {
  padding-left: 0;
  margin: 0;
  text-align: left;
}

.styles-list li {
  font-size: 1rem;
  line-height: 1.4;
  color: var(--color-0);
  margin-bottom: 0.5rem;
  list-style: none;
}

.tips-list {
  list-style: none;
  counter-reset: step-counter;
  padding-left: 2rem;
}

.tips-list li {
  counter-increment: step-counter;
  position: relative;
	font-size: 1rem;
  line-height: 1.4;
  color: var(--color-0);
  margin-bottom: 0.5rem;
  list-style: none;
}

.tips-list li::before {
  content: counter(step-counter);
  position: absolute;
  left: -32px;
  top: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--blue-1);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}


</style>
